import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    contentChildren,
    effect,
    HostBinding,
    inject,
    input,
    OnInit,
    output,
    signal,
    TemplateRef,
    viewChild,
    ViewEncapsulation
} from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import {
    ConnectedPositioningStrategy,
    GridColumnDataType,
    HorizontalAlignment,
    IGridCellEventArgs,
    IgxGridComponent,
    VerticalAlignment
} from '@infragistics/igniteui-angular';
import { forkJoin, Observable, Subject } from 'rxjs';
import { debounceTime, finalize, map, switchMap } from 'rxjs/operators';

import { BaseEntityModel } from '../../core/abstractions/base-entity.model';
import { BaseEntityResource } from '../../core/abstractions/base-entity.resource';
import { BaseComponent } from '../../core/abstractions/base.component';
import { ApiResponseModel } from '../../models/core/api-response.model';
import { RelatedItemModel } from '../../models/core/related-item.model';
import { RoleModel } from '../../models/role.model';
import { OptionalType } from '../../models/types/optional.type';
import { ActionButtonsService } from '../../services/action-buttons.service';
import { AlertService } from '../../services/alert.service';
import { AuthService } from '../../services/auth.service';
import { ControllerService } from '../../services/controller.service';
import { GridSettingsService } from '../../services/grid-settings.service';
import { GridStatusService } from '../../services/grid-status.service';
import { NotificationService } from '../../services/notification.service';
import { RolesResource } from '../../services/resources/roles.resource';
import { TranslationService } from '../../services/translation.service';
import { UserSessionService } from '../../services/user-session.service';
import { relatedResources } from '../layouts/form-view/form-view.component';
import { speedDialButtons } from '../layouts/speed-dial-fab/speed-dial-fab.component';
import { GridColumnDirective } from './grid-column.directive';
import { GridCommandOptionEnum } from './models/enums/grid-command-option.enum';
import { NumberType } from './models/enums/number-type';
import { GridBooleanColumnConfig } from './models/grid-boolean-column.config';
import { GridColumnConfig } from './models/grid-column.config';
import { GridDateColumnConfig } from './models/grid-date-column.config';
import { GridNumberColumnConfig } from './models/grid-number-column.config';
import { GridConfig } from './models/grid.config';

// eslint-disable-next-line import/no-mutable-exports
export let counter = 0;

@Component({
    selector: 'sl-paged-grid',
    templateUrl: './paged-grid.component.html',
    styleUrls: ['./paged-grid.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PagedGridComponent<T extends BaseEntityModel> extends BaseComponent implements OnInit, AfterViewInit {
    @HostBinding('class') themesClass = 'seg-theme';

    readonly grid = viewChild.required('grid', { read: IgxGridComponent });
    readonly columnTemplates = contentChildren(GridColumnDirective);
    readonly config = input.required<GridConfig<T>>();
    readonly pageSize = signal<number>(10);
    readonly columns = signal<GridColumnConfig[]>([]);
    readonly data = signal<T[]>([]);
    readonly isLoaded = signal(false);

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    readonly onDoubleClick = output<IGridCellEventArgs>();

    canSelect = false;
    searchCompleted = new Subject<ApiResponseModel<T[]>>();
    DataType = GridColumnDataType;
    NumberType = NumberType;
    totalPages: number;
    currentPage = 1;
    isFirstPage = this.currentPage <= 1;
    totalCount: number;
    lCounter = counter;
    shouldPreventSettingsSave = false;
    canChangeColumnVisibility = false;
    canSaveRoleSettings = false;
    isDeleting = false;
    isLastPage: boolean;
    selectOptions = [5, 10, 15, 25, 50, 100, 500];
    searchTerm = '';
    roles: RoleModel[] = [];
    dropdownOverlaySettings = {
        positionStrategy: new ConnectedPositioningStrategy({
            horizontalDirection: HorizontalAlignment.Left,
            horizontalStartPoint: HorizontalAlignment.Right,
            verticalStartPoint: VerticalAlignment.Bottom
        })
    };

    get selectedRows(): any[] {
        return this.localSelectedRows;
    }

    private localSelectedRows: any[] = [];
    private originalHeaderTags: string[] = [];
    private searchSubject = new Subject();
    private currentRelatedActions: RelatedItemModel[] = [];

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly translationService = inject(TranslationService);
    private readonly alertService = inject(AlertService);
    private readonly actionButtonsService = inject(ActionButtonsService);
    private readonly controllerService = inject(ControllerService);
    private readonly authService = inject(AuthService);
    private readonly gridSettingsService = inject(GridSettingsService);
    private readonly rolesResource = inject(RolesResource);
    private readonly gridStatusService = inject(GridStatusService);
    private readonly notificationService = inject(NotificationService);
    private readonly userSessionService = inject(UserSessionService);

    constructor() {
        super();

        counter++;
        this.isLastPage = this.currentPage >= this.totalPages;
        const searchSubscription = this.searchSubject
            .pipe(
                debounceTime(100),
                switchMap(() =>
                    this.config().resource.search(
                        this.searchTerm,
                        this.currentPage,
                        this.pageSize(),
                        this.grid().filteringExpressionsTree.filteringOperands.length > 0
                            ? this.grid().filteringExpressionsTree
                            : undefined,
                        this.grid().sortingExpressions.length > 0 ? this.grid().sortingExpressions : undefined,
                        this.config()?.extraProperties
                    )
                )
            )
            .subscribe(res => this.handleSearchResult(res));

        effect(
            () => {
                if (this.pageSize() !== this.grid().perPage) {
                    this.pageSize.set(this.grid().perPage);
                    this.paginate(this.currentPage, true);
                }
            },
            { allowSignalWrites: true }
        );

        this.addSubscriptions(searchSubscription);
    }

    ngOnInit(): void {
        const rowSelection = this.grid().rowSelectionChanging.subscribe(e => {
            this.localSelectedRows = e.newSelection;
            this.updateRelatedActions();
        });
        const reloadSub = this.gridStatusService.onGridReload.subscribe(() => this.setPageContent());
        this.localSelectedRows = this.grid().selectedRows;

        this.rolesResource.getAll().subscribe(r => (this.roles = r.value));
        this.setupConfig();

        if (!this.config().name) {
            throw Error('PagedGridComponent: The grid name must be specified');
        }

        this.validatePermissions();
        this.columns()!.forEach(c => this.originalHeaderTags.push(c.header));
        this.updateHeaders();

        const languageSub = this.translationService.onLanguageChanged.subscribe(() => this.updateHeaders());

        this.addSubscriptions(languageSub, rowSelection, reloadSub);
    }

    ngAfterViewInit(): void {
        this.setPageContent();
    }

    updatePageLimits(): void {
        this.isFirstPage = this.currentPage === 1;
        this.isLastPage = this.currentPage === this.totalPages;
    }

    goToCreate(): void {
        this.router.navigate(['add'], { relativeTo: this.activatedRoute.parent }).then();
    }

    goToView(id?: any): void {
        if (id === undefined) {
            id = this.grid().selectedRows[0];
        }

        if (!!this.config()?.typeName) {
            this.controllerService.execute(new RelatedItemModel().withRequest(this.config()!.typeName, id));
        }
    }

    deleteSelected(): void {
        const entityResource = this.config().resource as BaseEntityResource<T>;
        this.alertService.showConfirm('General.AreYouSureRemove', undefined, () => {
            const selectedIds = this.grid().selectedRows;
            const requests: Observable<ApiResponseModel<boolean>>[] = [];
            this.isDeleting = true;
            this.grid().isLoading = true;

            selectedIds.forEach(id => requests.push(entityResource.remove(id)));

            forkJoin(requests)
                .pipe(
                    finalize(() => {
                        this.isDeleting = false;
                        this.setPageContent();
                    })
                )
                .subscribe();
        });
    }

    nextPage(): void {
        this.currentPage++;
        this.setPageContent();
    }

    previousPage(): void {
        this.currentPage--;
        this.setPageContent();
    }

    paginate(page: number, shouldRecalculate: boolean): void {
        this.currentPage = page;

        if (shouldRecalculate) {
            this.totalPages = Math.ceil(this.totalCount / this.pageSize());
        }

        this.setPageContent();
    }

    processData(): void {
        this.setPageContent();
    }

    onPageSizeChange(e: MatSelectChange): void {
        this.grid().perPage = e.value;
        this.pageSize.set(this.grid().perPage);

        this.currentPage = 1;

        this.paginate(this.currentPage, false);
    }

    processSorting(): void {
        this.processData();
    }

    doubleClick(e: IGridCellEventArgs): void {
        this.onDoubleClick.emit(e);

        if (this.config().hasViewButton) {
            this.goToView(e.cell.row.data.id);
        }
    }

    onSearchEnter(event: any): void {
        this.searchTerm = event.target.value;
        this.processData();
    }

    updateHeaders(): void {
        const requests: Observable<string>[] = [];

        this.originalHeaderTags.forEach(h => requests.push(this.translationService.getText(h)));

        forkJoin(requests)
            .pipe(
                map((resp: string[]) => {
                    for (let i = 0; i < resp.length; i++) {
                        this.columns()[i].header = resp[i];
                    }
                    // prevents ExpressionChangedAfterItHasBeenCheckedError
                    this.changeDetectorRef.detectChanges();
                }),
                finalize(() => requestAnimationFrame(() => this.grid().markForCheck()))
            )
            .subscribe();
    }

    saveRoleSettings(roleId: number): void {
        this.gridSettingsService.saveForRole(roleId, this.config().name, this.grid()).subscribe();
    }

    loadRoleSettings(roleId: number): void {
        this.gridSettingsService.loadForRole(roleId, this.config().name).subscribe(hadSettings => {
            if (!hadSettings) {
                this.notificationService.showAlert('General.NoSavedSettingsForRole');
            }
        });
    }

    resetRoleSettings(): void {
        this.shouldPreventSettingsSave = true;

        this.gridSettingsService.remove(this.config().name);
        setTimeout(() => location.reload(), 100);
    }

    resetColumns(columns: GridColumnConfig[]): void {
        const newOrderedColumns: GridColumnConfig[] = [];

        columns
            .sort((a, b) => (a.index > b.index ? 1 : -1))
            .forEach(c => {
                const column = this.columns().find(col => col.fieldName === c.fieldName);

                if (!!column) {
                    column.isPinned = c.isPinned;
                    column.isHidden = c.isHidden;
                    column.width = c.width;

                    newOrderedColumns.push(column);
                }
            });

        this.columns.set(newOrderedColumns);
        this.originalHeaderTags = [];

        this.grid().reflow();
        this.changeDetectorRef.detectChanges();
        this.columns().forEach(c => this.originalHeaderTags.push(c.header));
        this.updateHeaders();
    }

    getTemplateRef(columnName: string): OptionalType<TemplateRef<any>> {
        const template = this.columnTemplates().find(c => c.slGridColumn() === columnName);

        if (!!template) {
            return template.templateRef;
        }

        return undefined;
    }

    getAsNumberColumn(column: GridColumnConfig): GridNumberColumnConfig {
        return column as GridNumberColumnConfig;
    }

    getAsBooleanColumn(column: GridColumnConfig): GridBooleanColumnConfig {
        return column as GridBooleanColumnConfig;
    }

    getAsDateColumn(column: GridColumnConfig): GridDateColumnConfig {
        return column as GridDateColumnConfig;
    }

    private setupConfig(): void {
        this.grid().clipboardOptions.copyFormatters = false;
        this.grid().clipboardOptions.copyHeaders = false;
        this.pageSize.set(this.config()!.pageSize);
        // Only load columns which we have permission for
        this.columns.set(this.config()!.columns.filter(c => !c.requiredPermission || this.authService.hasPermission(c.requiredPermission)));
    }

    private validatePermissions(): void {
        if (!this.config().name) {
            return;
        }

        let permission = '';

        switch (this.config().name) {
            case 'shareholders':
                permission = 'ShareholderGridColumnsVisibility';
                break;
            case 'traders':
                permission = 'TradersGridColumnsVisibility';
                break;
            case 'transactions':
                permission = 'TransactionsGridColumnsVisibility';
                break;
            case 'users':
                permission = 'UsersGridColumnsVisibility';
                break;
            case 'gm-shareholders':
                permission = 'ShareholderGridColumnsVisibility';
                break;
            default:
                break;
        }

        if (!permission) {
            return;
        }

        this.canChangeColumnVisibility = this.authService.hasPermission(permission);
        // Use the basic data command to determine this permission
        this.canSaveRoleSettings = this.authService.hasPermission('UpdateBasicDataCommand');
    }

    private setPageContent(): void {
        this.searchSubject.next(undefined);
    }

    private handleSearchResult(res: ApiResponseModel<T[]>): void {
        this.data.set(res.value);

        this.totalCount = res.totalRecords ?? 0;
        this.totalPages = res.totalPages ?? 0;
        this.currentRelatedActions = this.userSessionService.hasApprovalRight
            ? res.relatedActions
            : res.relatedActions.filter(ra => ra.commandName !== 'ApproveTransactionCommand');
        this.currentRelatedActions = this.currentRelatedActions || [];

        // If some action has parameter, we allow the column selection (#5150)
        if (this.currentRelatedActions.some(ra => ra.uri.includes('/{0}/'))) {
            this.canSelect = true;
        }

        speedDialButtons.set(this.actionButtonsService.convert(this.currentRelatedActions));

        if (!!res.relatedResources) {
            relatedResources.set(res.relatedResources);
        }

        this.updateRelatedActions();
        this.searchCompleted.next(res);
        this.updatePageLimits();
        this.isLoaded.set(true);
    }

    private updateRelatedActions(): void {
        this.currentRelatedActions.forEach(ra => {
            const commandOption = this.getCommandOption(ra.commandName);

            if (!!this.config && !!this.config()!.relatedActionsHandler) {
                this.config()!.relatedActionsHandler!(ra, this);
            }

            if (!ra.referencedButton) {
                return;
            }

            switch (commandOption) {
                case GridCommandOptionEnum.Delete:
                    ra.referencedButton.clickFunction = () => this.deleteSelected();
                    ra.referencedButton.isDisabled = this.grid().selectedRows.length < 1;
                    break;
                case GridCommandOptionEnum.ExportCsv:
                    ra.referencedButton.beforeClickFunction = () =>
                        (ra.data = {
                            description: undefined,
                            page: this.currentPage,
                            pageSize: this.pageSize(),
                            filters:
                                this.grid().filteringExpressionsTree.filteringOperands.length > 0
                                    ? this.grid().filteringExpressionsTree
                                    : undefined,
                            sortings: this.grid().sortingExpressions.length > 0 ? this.grid().sortingExpressions : undefined
                        });
                    break;
                default:
                    break;
            }
        });
    }

    private getCommandOption(commandName: string): GridCommandOptionEnum {
        let suffix: string;

        switch (this.config()?.name) {
            case 'shareholders':
                suffix = 'Shareholder';
                break;
            case 'gm-shareholders':
                suffix = 'GmShareholder';
                break;
            case 'traders':
                suffix = 'Trader';
                break;
            case 'transactions':
                suffix = 'Transaction';
                break;
            case 'users':
                suffix = 'User';
                break;
            default:
                suffix = '';
                break;
        }

        if (!suffix || !commandName.includes(suffix)) {
            return GridCommandOptionEnum.NotFound;
        }

        if (commandName.includes('Create')) {
            return GridCommandOptionEnum.Create;
        }

        if (commandName.includes('Delete')) {
            return GridCommandOptionEnum.Delete;
        }

        if (commandName.includes('List') && commandName.includes('Csv')) {
            return GridCommandOptionEnum.ExportCsv;
        }

        return GridCommandOptionEnum.NotFound;
    }
}
